import { useEffect, useRef, useState, MutableRefObject, Suspense } from 'react'
import { useDispatch } from 'react-redux';
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import axios from 'axios';
import { ErrorMessage, Field, Form, Formik } from 'formik'
import Cookies from 'js-cookie';
import { EmailSchema, RequiredField, YupObject } from '@/Utils/ValidationSchema'
import FormikInput from '@/Component/Common/Forms/FormikInput'
import PrimaryButton from '@/Component/Common/Forms/PrimaryButton'
import usePost from '@/Utils/Hooks/usePost'
import { LoginAPI } from '@/Utils/APIRequest/Endpoints'
import { EmailSVG, EyeCloseSVG, EyeOpenSVG, PasswordSVG } from '@/Component/Common/SVGs/AuthSVG'
import FormLabel from '@/Component/Common/Forms/FormLabel';
import { LoaderSVG } from '@/Component/Common/SVGs/CommonSVG';
import ValidationMsg from '@/Utils/ValidationSchema/ValidationMsg';
import { CustomToast } from '@/Utils/CustomToast';
import { getUserDetails } from '@/lib/Features/UserDetails/userDetails'
import SkeletonLoader from '@/Layout/SkeletonLoader';
import { getOAuthConsentRedirectPath, persistOAuthParams } from '@/Utils/Other/OAuthRedirect';


import type { ReCAPTCHA as ReCAPTCHAType } from 'react-google-recaptcha';
import ReCaptchaWrapper from './ReCaptchaWrapper';

const CustomCheckbox = ({ field, label }) => (
    <label className="flex items-center space-x-2 cursor-pointer">
        <div className="relative">
            <input type="checkbox" {...field} checked={field.value} className="hidden peer" />
            <div className="w-4 h-4 border border-dark-gray rounded-sm flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary transition-all">
                <svg className="w-4 h-4 text-white peer-checked:block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
            </div>
        </div>
        <span>{label}</span>
    </label>
);

const LoginFormContent = (props) => {
    const { role, translations, locale, skeletonLoading } = props;
    const router = useRouter();
    const dispatch = useDispatch();
    const recaptchaRef = useRef<any>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [showRecaptcha, setShowRecaptcha] = useState(false);
    const siteKey: any = process.env.CAPTCHA_SITEKEY;
    const searchParams = useSearchParams();

    const [visiblePassword, setVisiblePassword] = useState('password');

    const { data, isPending, mutate } = usePost(LoginAPI, null, null, null);

    useEffect(() => {
        persistOAuthParams(window.location.search);
    }, [searchParams]);

    useEffect(() => {
        if (data?.status === 200) {
            if (data?.data?.role == 'admin' || data?.data?.role == 'brand' || data?.data?.role == 'influencer') {
                Cookies.remove('verifyUAT');
                Cookies.remove('role');
                let userDetail = {
                    role: data?.data?.role,
                    pending_steps: data?.data?.pending_steps
                }
                Cookies.set('user_info', JSON.stringify(userDetail), { expires: 7, secure: true });
                if (!data?.data?.is_verify_otp) {
                    Cookies.remove('admin_email');
                    Cookies.remove('forgot_email');
                    Cookies.set('register_email', data?.data?.email)
                    router.push(`/register/${data?.data?.role}/verify-otp`)
                } else {
                    if (data?.data?.role == 'admin') {
                        Cookies.remove('register_email');
                        Cookies.remove('forgot_email');
                        Cookies.set('admin_email', data?.data?.email);
                        router.push("/verify-otp");
                    }

                    if (data?.data?.role == 'brand') {
                        Cookies.set('bat', data?.data?.access_token, { expires: 7, secure: true })
                    } else if (data?.data?.role == 'influencer') {
                        Cookies.set('iat', data?.data?.access_token, { expires: 7, secure: true })
                    }

                    if (data?.data?.role !== 'admin' && data?.data?.pending_steps < 3) {
                        router.push(`/register/${data?.data?.role}/steps`)
                    } else if (data?.data?.role !== 'admin') {
                        const consentUrl = getOAuthConsentRedirectPath(searchParams);

                        if (consentUrl) {
                            Cookies.remove('pending_redirect');
                            router.push(consentUrl);
                            return;
                        }

                        const pendingRedirect = Cookies.get('pending_redirect');
                        if (pendingRedirect) {
                            Cookies.set('pending_redirect_handled', 'true', { expires: 1 / 1440 });
                            router.push(pendingRedirect);
                            Cookies.remove('pending_redirect');
                        } else {
                            router.push(`/${data?.data?.role}/dashboard`);
                        }
                    }
                    // } else if (data?.data?.role !== 'admin') {

                    //     console.log("data?.data?.role", data?.data?.role);

                    //     // Check for pending redirect URL from cookie
                    //     const pendingRedirect = Cookies.get('pending_redirect');
                    //     if (pendingRedirect) {
                    //         // Set a flag to indicate we're handling a pending redirect
                    //         Cookies.set('pending_redirect_handled', 'true', { expires: 1 / 1440 }); // 1 minute (1/1440 days)
                    //         router.push(pendingRedirect);
                    //         Cookies.remove('pending_redirect');
                    //     } else {
                    //         router.push(`/${data?.data?.role}/dashboard`)
                    //     }
                    // }
                }

                const userPayload: any = {
                    uid: data?.data?.id,
                    name: data?.data?.role === 'influencer' ? data?.data?.display_name : `${data?.data.first_name} ${data?.data.last_name}`,
                    withAuthToken: true
                };

                if (data?.data?.image) {
                    userPayload['avatar'] = data?.data?.image
                }

                const createUser: any = axios.post('/api/chat', { data: userPayload, endpoint: "users", action: "checkOrCreate" });
                // let chatAuthToken = createUser?.data?.authToken;
                // Cookies.set("cc_auth", 
                //     chatAuthToken, {
                //     expires: 7,
                //     secure: true,
                //     sameSite: "strict"
                // });
                // console.log("createUsercreateUser else", createUser?.data, "chatAuthToken", chatAuthToken);

                dispatch(getUserDetails(data?.data))
            }
        } else if (data && data?.status !== 200) {
            // recaptchaRef.current.reset();         // ✅ Fully works now
            // setRecaptchaToken(null);
        }
    }, [isPending]);

    const handleFormFocus = () => {
        setShowRecaptcha(true);
    };

    const handleChange = (e, setFieldValue) => {
        const { name, value } = e.target
        setVisiblePassword('password')
        setFieldValue(name, value)
    }

    const handleRecaptchaChange = (token: string | null) => {
        setRecaptchaToken(token);
    };

    return (
        <Formik enableReinitialize initialValues={{ email: '', password: '', remember_me: false }}
            validationSchema={YupObject({
                email: EmailSchema(translations),
                password: RequiredField(translations)
            })}
            onSubmit={(values: any) => {
                persistOAuthParams(window.location.search);
                values['role'] = role;
                values['language_code'] = locale;
                values['recaptcha_token'] = recaptchaToken;

                if (values.remember_me) {
                    localStorage.setItem('rememberMeEmail', values.email);
                    localStorage.setItem('rememberMePassword', values.password);
                } else {
                    localStorage.removeItem('rememberMeEmail');
                    localStorage.removeItem('rememberMePassword');
                }

                mutate(values);
            }}
        >
            {({ errors, touched, setFieldValue }) => {
                useEffect(() => {
                    const savedEmail = localStorage.getItem('rememberMeEmail');
                    const savedPassword = localStorage.getItem('rememberMePassword');

                    if (savedEmail && savedPassword) {
                        setFieldValue('email', savedEmail);
                        setFieldValue('password', savedPassword);
                        setFieldValue('remember_me', true);  // Check the "Remember Me" box
                    }
                }, [setFieldValue])
                return (
                    <Form className='sm:mt-10 xs:mt-8 mt-5' onFocus={handleFormFocus}>
                        <div className="xs:mb-7 mb-4">
                            <FormLabel title={translations.email} skeletonLoading={skeletonLoading} />
                            <div className="relative">
                                <EmailSVG customClass='absolute top-[50%] translate-y-[-50%] right-3' />
                                <FormikInput customClass={'pr-11'} inputProps={{ type: "email", name: "email", placeholder: `${translations.emailPlaceholder}`, autoComplete: "new-email" }} skeletonLoading={skeletonLoading} />
                            </div>
                            <ErrorMessage name="email" component="div" render={(msg) =>
                                <ValidationMsg errorMsg={msg} />
                            } />
                        </div>
                        <div className="xs:mb-7 mb-4">
                            <FormLabel title={translations.password} skeletonLoading={skeletonLoading} />
                            <div className="relative">
                                <PasswordSVG customClass='absolute top-[50%] translate-y-[-50%] right-3' />
                                <FormikInput customClass={`pr-20`} inputProps={{ type: visiblePassword, name: "password", onChange: (e) => handleChange(e, setFieldValue), placeholder: `${translations.passwordPlaceholder}`, autoComplete: "new-password" }} skeletonLoading={skeletonLoading} />
                                <span onClick={() => setVisiblePassword((prev) => prev == 'password' ? 'text' : 'password')}>
                                    {visiblePassword == 'text' ? <EyeOpenSVG customClass='absolute top-[50%] translate-y-[-50%] right-10 cursor-pointer' /> : <EyeCloseSVG customClass='absolute top-[50%] translate-y-[-50%] right-10 cursor-pointer' />}
                                </span>
                            </div>
                            <ErrorMessage name="password" component="div" render={() =>
                                <ValidationMsg errorMsg={translations.passwordRequired} />
                            } />
                        </div>
                        {/* {showRecaptcha && (
                            <div className="col-span-12">
                                <ReCaptchaWrapper
                                    ref={recaptchaRef}
                                    sitekey={siteKey}
                                    onChange={handleRecaptchaChange}
                                />
                            </div>
                        )} */}
                        <div className='flex justify-between items-center 2xl:my-5 my-3'>
                            <div className="flex items-center text-sm">
                                {skeletonLoading ? <SkeletonLoader width="135" height={24} rows={1} /> :
                                    <Field name="remember_me" component={CustomCheckbox} label={translations.rememberMe} />}
                            </div>
                            {role == 'admin' ? null :
                                <>
                                    {skeletonLoading ? <SkeletonLoader width="135" height={24} rows={1} /> :
                                        <Link href={'/forgot-password'} className='focus-visible:outline-none text-sm'>{translations.forgotPassword}</Link>}
                                </>
                            }
                        </div>
                        <PrimaryButton customClass='w-full mt-5 flex items-center justify-center !py-3' buttonType='submit' disabled={isPending} skeletonLoading={skeletonLoading} width={'100%'}>
                            {translations.loginNow}
                            {isPending && <LoaderSVG customClass='ml-[10px]' />}
                        </PrimaryButton>
                    </Form>

                )
            }}
        </Formik >
    )
}

const LoginForm = (props) => {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <LoginFormContent {...props} />
        </Suspense>
    );
};

export default LoginForm
